/*
 * Right sidebar, narrow, stuck to the top even when rest of the page scrolls.
 */

import dynamic from 'next/dynamic'
import React, { ReactNode } from 'react'
import styled from 'styled-components'

import { useScroll } from '@hozana/hooks/useScroll'
import { useSelector } from '@hozana/redux/hooks'
import type { TBreakpoint, TResponsiveProps } from '@hozana/styling/responsive'

import { Div, TDivProps } from 'elements/layout/Div'

import { ALERT_BANNER_DESKTOP_HEIGHT } from 'general/managers/alert/AlertBanner'
import { isAlertBannerOpen as isAlertBannerOpenSelector } from 'general/selectors'

const StickyBox = dynamic(() => import('react-sticky-box'), { loading: () => null })

const responsiveProps = { w: 'width' } as const

const StyledDiv = styled.div`
  height: 100%;
  ${__SERVER__ &&
  `
    & > div {
      height: 100%;
    }
  `}
`

type TStyledAsideProps = {
  offsetTop: number
} & TDivProps

const Aside = styled(Div).attrs<TStyledAsideProps, Partial<TStyledAsideProps>>({ as: 'aside' })`
  ${({ offsetTop }) =>
    __SERVER__ &&
    `
    position: sticky;
    top: ${offsetTop}px;
  `}
`

type TSidebarProps = {
  visible?: TBreakpoint[]
  offsetTop?: number
  offsetBottom?: number
  children: ReactNode | ((p: { distanceFromTop: number }) => ReactNode)
} & TResponsiveProps<typeof responsiveProps>

export const Sidebar: TFCC<TSidebarProps> = ({
  visible = ['lg', 'xl'],
  children,
  w = '265px',
  offsetTop = 55,
  offsetBottom = 70,
}) => {
  const { pageYOffset } = useScroll()
  const isAlertBannerOpen = useSelector(isAlertBannerOpenSelector)

  const correctedOffestTop = offsetTop + (isAlertBannerOpen ? ALERT_BANNER_DESKTOP_HEIGHT : 0)

  return (
    <StyledDiv>
      <StickyBox offsetTop={correctedOffestTop} offsetBottom={offsetBottom}>
        <Aside offsetTop={correctedOffestTop} visible={visible} w={w}>
          {typeof children === 'function' ? children({ distanceFromTop: pageYOffset }) : children}
        </Aside>
      </StickyBox>
    </StyledDiv>
  )
}
